import {FormHelperText, Grid, IconButton, useTheme} from "@mui/material";
import Typography from "@mui/material/Typography";
import {OutlinedAddButton} from "../../shared/components/buttons/OutlinedAddButton";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {retrieveError} from "../../shared/components/forms/formUtils";
import {useFieldArray, useFormContext} from "react-hook-form";
import {UserDetails} from "./User";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {LightWeightApplication} from "../applications/Application";
import {useQuery} from "react-query";
import {CenteredCircularProgress} from "../../shared/components/progress/CenteredCircularProgress";
import {ApplicationSelectField} from "../applications/ApplicationSelectField";
import {MuiRhfSelectField} from "../../shared/components/forms/MuiRhfSelectField";
import {palette} from "../../shared/technical/style/theme";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {Group} from "./Group";
import {getAllApplicationUserGroups} from "../applications/groups/ApplicationUserGroupApi";
import {ApplicationUserGroup} from "../applications/groups/ApplicationUserGroup";

export function GroupsForm(props: {
  disabled?: boolean
}) {
  const {t} = useTranslation();
  const theme = useTheme();
  const {control, formState: {errors}} = useFormContext<UserDetails>();
  const {fields, append, remove} = useFieldArray<UserDetails>({
    control,
    name: "groups"
  });

  return (
    <Grid container={true}>
      <Grid item={true} lg={12} sx={{
        display: 'flex',
        alignItems: 'center',
        '& > *': {
          marginRight: theme.spacing(2)
        }
      }}>
        <Typography variant={'h4'}>{t('ApplicationUserGroups.titleLong') + ':'}</Typography>
        {!props.disabled &&
          <OutlinedAddButton onClick={() => append({} as Group)} title={t('ApplicationUserGroups.buttonCreate')}/>
        }
      </Grid>
      {retrieveError('groups', errors) &&
        <FormHelperText error={true}>{retrieveError('groups', errors)}</FormHelperText>
      }
      {fields.map((group: any, index: number) =>
        <
          GroupForm group={group} index={index} remove={remove} disabled={props.disabled} key={index}/>
      )}
    </Grid>
  )
}

export function GroupForm(props: {
  group : Group,
  index: number,
  remove: (index?: number | number[]) => void,
  disabled?: boolean
}) {
  const theme = useTheme();
  const axios = useAxios();
  const {watch} = useFormContext<UserDetails>();

  const {t} = useTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const applicationWatch: LightWeightApplication | undefined = watch(`groups.${props.index}.application`);

  const {isLoading} = useQuery(
    ["getAllApplicationUserGroups", applicationWatch?.id],
    () => getAllApplicationUserGroups(axios, applicationWatch.id!!),
    {
      enabled: !!applicationWatch?.id,
      onSuccess: (userGroups: ApplicationUserGroup[]) => {
        const groups: Group[] = userGroups.map(userGroup => {
          return  {application: userGroup.application, groupName: userGroup.name};
        });
        setGroups(groups);
      },
      keepPreviousData: true
    }
  );

  if (isLoading) {
    return <CenteredCircularProgress />
  }

  return (
    <Grid container={true} spacing={3} sx={{marginTop: theme.spacing(3),}} key={props.group.id}>
      <Grid item={true} xs={5}>
        <ApplicationSelectField name={`groups.${props.index}.application`} disabled={props.disabled} />
      </Grid>
      <Grid item={true} xs={5}>
        <MuiRhfSelectField
          name={`groups.${props.index}.groupName`}
          label={t("ApplicationUserGroup.titleEdit")}
          list={groups.map(group => group.groupName)}
          getListLabel={(groups) => groups}
          getListKey={(groups) => groups}
          disabled={props.disabled}
        />
      </Grid>
      {!props.disabled &&
        <Grid item={true} xs={1} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <IconButton
            onClick={() => props.remove(props.index)}
            sx={{color: palette.primary,}}
            size="large">
            <DeleteOutlineIcon />
          </IconButton>
        </Grid>
      }
    </Grid>
  );
}
